/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useRef, forwardRef, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'

// Project Components
import LinksList from '@consteed/overrides/components/links-list'

// Components
import {
    Box,
    Container,
    SimpleGrid,
    Fade,
    Flex,
    Stack,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    Center,
    Spinner,
    Text,

    // Hooks
    useTheme,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@consteed/overrides/components/link'
import {ImageTextTile} from '@consteed/overrides/page-designer/assets/image-text-tile'
import HighlightText from '@consteed/frontend/components/pageDesigner/RTE/HighlightText'
// Others
import {ChevronDownIcon} from '@salesforce/retail-react-app/app/components/icons'
import {generalUrlBuilder} from '../../utils/url'
import useShrinkedHeader from '@consteed/overrides/hooks/use-shrinked-header'

const MAXIMUM_NUMBER_COLUMNS = 3

const ChevronIconTrigger = forwardRef(function ChevronIconTrigger(props, ref) {
    return (
        <Box {...props} ref={ref}>
            <ChevronDownIcon />
        </Box>
    )
})

const ListMenuTrigger = ({item, name, isOpen, onOpen, onClose, hasItems, isActive}) => {
    const isShrunk = useShrinkedHeader()
    const styles = useMultiStyleConfig('ListMenu', {isShrunk, isActive})

    const keyMap = {
        Escape: () => onClose(),
        Enter: () => onOpen()
    }

    return (
        <Box {...styles.listMenuTriggerContainer}>
            <Link
                as={RouteLink}
                to={generalUrlBuilder(item)}
                onMouseOver={onOpen}
                {...styles.listMenuTriggerLink}
                {...(hasItems ? {name: name + ' __'} : {name: name})}
                {...(isOpen ? styles.listMenuTriggerLinkActive : {})}
                className={isActive ? 'menuLinkActive' : ''}
            >
                {name}
            </Link>

            {hasItems && (
                <PopoverTrigger>
                    <Link
                        as={RouteLink}
                        to={'#'}
                        onMouseOver={onOpen}
                        onKeyDown={(e) => {
                            keyMap[e.key]?.(e)
                        }}
                        {...styles.listMenuTriggerLinkIcon}
                    >
                        <Fade in={hasItems}>
                            <ChevronIconTrigger {...styles.selectedButtonIcon} />
                        </Fade>
                    </Link>
                </PopoverTrigger>
            )}
        </Box>
    )
}
ListMenuTrigger.propTypes = {
    item: PropTypes.object,
    name: PropTypes.string,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    hasItems: PropTypes.bool,
    isActive: PropTypes.bool
}

const ListMenuContent = ({name, maxColumns, items, itemsKey, onClose, initialFocusRef}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const {locale, formatMessage} = useIntl()

    return (
        <PopoverContent
            data-testid="popover-menu"
            {...baseStyle.popoverContent}
            className={'pdcOuterBox'}
        >
            <PopoverHeader borderBottom="0" paddingTop="3rem">
                <Container {...baseStyle.popoverContainer} paddingBottom="0">
                    <Text textStyle="light3Xlarge" color={'black'}>
                        {formatMessage({
                            id: 'general.brand.name',
                            defaultMessage: 'Consteed'
                        })}{' '}
                        <HighlightText fontWeight="500">{name}</HighlightText>
                    </Text>
                </Container>
            </PopoverHeader>
            <PopoverBody>
                <Container as={Stack} {...baseStyle.popoverContainer}>
                    <SimpleGrid
                        spacing={8}
                        justifyContent={'left'}
                        gridTemplateColumns={`repeat(${
                            items.length > maxColumns ? maxColumns : items.length
                        }, minmax(0, 33%))`}
                    >
                        {items.map((item, index) => {
                            const {id, name, image} = item
                            const items = item[itemsKey]
                            const key = index + '_' + id

                            if (image) {
                                return (
                                    <Link
                                        key={key}
                                        to={generalUrlBuilder(item, locale)}
                                        onClick={onClose}
                                    >
                                        <ImageTextTile {...item} />
                                    </Link>
                                )
                            }

                            const heading = {
                                href: generalUrlBuilder(item, locale),
                                text: name,
                                styles: {
                                    fontSize: 'md',
                                    marginBottom: 2
                                }
                            }

                            const links = items
                                ? items.map((item) => {
                                      const {name} = item
                                      return {
                                          href: generalUrlBuilder(item, locale),
                                          text: name,
                                          styles: {
                                              fontSize: 'md',
                                              paddingTop: 3,
                                              paddingBottom: 3
                                          }
                                      }
                                  })
                                : []
                            return (
                                <LinksList
                                    key={key}
                                    heading={heading}
                                    links={links}
                                    color={'gray.900'}
                                    onLinkClick={onClose}
                                    {...(index === 0 ? {headingLinkRef: initialFocusRef} : {})}
                                />
                            )
                        })}
                    </SimpleGrid>
                </Container>
            </PopoverBody>
        </PopoverContent>
    )
}
ListMenuContent.propTypes = {
    items: PropTypes.array,
    maxColumns: PropTypes.number,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    itemsKey: PropTypes.string,
    name: PropTypes.string
}

const ListMenuPopover = ({items, item, name, itemsKey, maxColumns, isActive}) => {
    const initialFocusRef = useRef()
    const {isOpen, onClose, onOpen} = useDisclosure()
    return (
        <Box onMouseLeave={onClose}>
            <Popover
                isLazy
                placement={'bottom-start'}
                initialFocusRef={initialFocusRef}
                onOpen={onOpen}
                onClose={onClose}
                isOpen={isOpen}
                variant="fullWidth"
            >
                <Fragment>
                    <ListMenuTrigger
                        item={item}
                        name={name}
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                        hasItems={!!items}
                        isActive={isActive}
                    />
                    {items && (
                        <ListMenuContent
                            name={name}
                            items={items}
                            itemsKey={itemsKey}
                            initialFocusRef={initialFocusRef}
                            onClose={onClose}
                            maxColumns={maxColumns}
                        />
                    )}
                </Fragment>
            </Popover>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    items: PropTypes.array,
    item: PropTypes.object,
    name: PropTypes.string,
    maxColumns: PropTypes.number,
    itemsKey: PropTypes.string,
    isActive: PropTypes.bool
}

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({root, activePage, maxColumns = MAXIMUM_NUMBER_COLUMNS}) => {
    const itemsKey = 'categories'
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const [ariaBusy, setAriaBusy] = useState(true)
    const intl = useIntl()

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    return (
        <nav
            id="list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Flex {...baseStyle.container}>
                {root?.[itemsKey] ? (
                    <Stack direction={'row'} spacing={{xl: 6}} {...baseStyle.stackContainer}>
                        {root?.[itemsKey]?.map?.((item) => {
                            const {id, name} = item
                            return (
                                <Box key={id}>
                                    <ListMenuPopover
                                        key={id}
                                        maxColumns={maxColumns}
                                        item={item}
                                        name={name}
                                        items={item?.[itemsKey]}
                                        itemsKey={itemsKey}
                                        isActive={id === activePage}
                                    />
                                </Box>
                            )
                        })}
                    </Stack>
                ) : (
                    <Center p="2">
                        <Spinner size="lg" />
                    </Center>
                )}
            </Flex>
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    root: PropTypes.object,
    activePage: PropTypes.string,
    /**
     * The maximum number of columns that we want to use per row in the menu.
     */
    maxColumns: PropTypes.number
}

export default ListMenu
